import { CustomCheckboxField } from "@/app/features/reports/components/custom-fields/custom-checkbox-field"
import { CustomRadioField } from "@/app/features/reports/components/custom-fields/custom-radio-field"
import { CustomTextAreaField } from "@/app/features/reports/components/custom-fields/custom-textarea-field"
import { getCustomFieldId } from "@app.raytd.com/common/utils/test-suites/getFieldRequirement"
import { useAddCustomFieldOptionMutation } from "@app.raytd.com/store"
import { memo, useCallback, useMemo } from "react"
import { Control, FieldValues, useController, useFormContext } from "react-hook-form"
import { toast } from "sonner"
import { CustomField, TestSuite } from "store/src/lib/tests/entity"
import { CustomDateField } from "./custom-fields/custom-date-field"
import { CustomNumberField } from "./custom-fields/custom-number-field"
import { CustomSelectField } from "./custom-fields/custom-select-field"
import { CustomTextField } from "./custom-fields/custom-text-field"
import { CustomToggleField } from "./custom-fields/custom-toggle-field"
import FieldContainer, { FieldContainerProps } from "./test-form/field-container"


export type CustomFieldProps = {
  control: any;
  name: string;
  label: string;
  description: string;
  showDescription?: boolean;
  info: string;
  required: boolean;
  placeholder?: string;
  isValid: boolean;
  showFormMessage?: boolean;
  ref?: React.RefObject<HTMLInputElement>;
  maxLength?: number;
}

interface CustomFieldsContainerProps {
  testSuite: TestSuite
  name: string;
  enableRightComponent?: boolean;
  onCustomFieldOptionAdded?: (testSuiteId: number, fieldId: string, value: string) => Promise<{ success: boolean, field_id: string, data: any }>;
  FieldContainerComponent?: React.ComponentType<FieldContainerProps>;
  enableFastFillHighlighting?: boolean;
}

export function CustomFieldsContainer({
  name = 'custom',
  testSuite: testSuiteData,
  enableRightComponent = false,
  onCustomFieldOptionAdded,
  FieldContainerComponent = FieldContainer,
  enableFastFillHighlighting = true
}: CustomFieldsContainerProps) {
  const { control } = useFormContext();

  if (!testSuiteData?.custom_fields?.length) return null;
  if (!testSuiteData || !testSuiteData.custom_fields || testSuiteData.custom_fields.length === 0) return null

  const sortedCustomFields = useMemo(() =>
    [...testSuiteData.custom_fields].sort((a, b) => a.field_order - b.field_order),
    [testSuiteData.custom_fields]
  );

  // const getFieldValidation = useCallback((field: any) => {
  //   const fieldValue = watch(`${name}.${field.field_id}`);
  //   const fieldError = errors[name]?.[field.field_id] ?? errors[`${name}.${field.field_id}`];

  //   // if (field.type === 'select' && field.selection_type === 'multiple') {
  //   //   console.debug('custom field validation', { field, required: field.required, fieldValue, fieldError });
  //   //   console.debug('isvalid', fieldValue?.length > 0 && !fieldError);
  //   // }


  //   // Field is valid if it has a value and no errors
  //   if (field.required) {
  //     const fieldIsNaN = field.type === 'number' && Number.isNaN(fieldValue);
  //     // console.debug('custom field validation', { 
  //     //   label:field.label ,
  //     //   field, fieldValue, 
  //     //   name,
  //     //   fieldError, fieldIsNaN,
  //     //    errors });
  //     return (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && !fieldIsNaN) && !fieldError;
  //   }
  //   // Optional fields are valid if they're empty or have no errors
  //   return !fieldValue || !fieldError;
  // }, [watch, errors, name]);


  const [addCustomFieldOption, { isLoading }] = useAddCustomFieldOptionMutation();

  const handleAddOption = useCallback(async (fieldId: string, displayName: string) => {
    try {

      if (!onCustomFieldOptionAdded) {
        console.error('onCustomFieldOptionAdded is not defined');
        toast.error('Error adding custom field option');
        return;
      }


      const fieldIdWithoutCustom = getCustomFieldId(fieldId);

      const result = await onCustomFieldOptionAdded?.(testSuiteData.id, fieldIdWithoutCustom, displayName);

      // Handle success
      console.log('Option added:', result?.data);
      return { success: true, field_id: result.field_id, data: result.data };
    } catch (error) {
      // Handle error
      console.error('Failed to add option:', error);
      throw error;
    }
  }, [addCustomFieldOption, testSuiteData.id]);

  return (
    <div className="">
      {sortedCustomFields
        .filter(field => field.active)
        .map((field) => (
          <CustomFieldItem
            key={field.field_id}
            control={control}
            field={field}
            namePrefix={name}
            onAddOption={handleAddOption}
            FieldContainerComponent={FieldContainerComponent}
            enableRightComponent={enableRightComponent}
            enableFastFillHighlighting={enableFastFillHighlighting}
          />
        ))}
    </div>
  );

  // const renderField = useCallback((field: any) => {
  //   const commonProps: CustomFieldProps & { key: string; } = {
  //     key: field.field_id,
  //     control: control,
  //     name: `${name}.${field.field_id}`,
  //     label: field.label,
  //     description: field.description,
  //     info: field.info,
  //     required: field.required,
  //     placeholder: field.placeholder || undefined,
  //     isValid: getFieldValidation(field),
  //     showDescription: false,
  //     showFormMessage: false
  //   }

  //   switch (field.type) {
  //     case 'text':
  //       return <CustomTextField {...commonProps} />
  //     case 'number':
  //       return <CustomNumberField {...commonProps} decimalPlaces={field.decimal_places} />
  //     case 'date':
  //       return <CustomDateField {...commonProps} />
  //     case 'select':
  //       return (
  //         <CustomSelectField
  //           {...commonProps}
  //           options={field.data}
  //           selectionType={field.selection_type}
  //           allowAdd={field.allow_add}
  //           onAddOption={handleAddOption}
  //         />
  //       )
  //     case 'radio':
  //       return <CustomRadioField {...commonProps} options={field.data} />
  //     case 'checkbox':
  //       return <CustomCheckboxField {...commonProps} />
  //     case 'switch':
  //       return <CustomToggleField {...commonProps} />
  //     case 'textarea':
  //       return <CustomTextAreaField
  //         {...commonProps}
  //         options={field.data}
  //         allowAdd={field.allow_add}
  //         onAddOption={handleAddOption}
  //       />
  //     default:
  //       return <div key={field.field_id}>Unknown field type: {field.type}</div>
  //   }
  // }, [control, name, getFieldValidation, handleAddOption]);

  // return (
  //   <div className="space-y-4">
  //     {sortedCustomFields
  //       .filter(field => field.active)
  //       .map((field) => {
  //         return (
  //           <FieldContainerComponent
  //             key={`${name}.${field.field_id}`}
  //             field_id={`${name}.${field.field_id}`}
  //             showRightComponent={enableRightComponent}
  //             isFastFill={field.type === 'textarea' && field.data?.length > 0}
  //           >
  //             {renderField(field)}
  //           </FieldContainerComponent>
  //         )
  //       })}


  //   </div>
  // )
}

interface CustomFieldItemProps {
  field: CustomField;
  control: Control<FieldValues>;
  namePrefix: string;
  onAddOption: (fieldId: string, displayName: string) => Promise<{ success: boolean, field_id: string, data: any }>;
  FieldContainerComponent: React.ComponentType<FieldContainerProps>;
  enableRightComponent: boolean;
  enableFastFillHighlighting: boolean;
}

const CustomFieldItem = memo(({ field, namePrefix, control, onAddOption, FieldContainerComponent, enableRightComponent, enableFastFillHighlighting }: CustomFieldItemProps) => {
  const fieldName = `${namePrefix}.${field.field_id}`;

  // useController is the key. It isolates re-renders to this component only.
  const { field: controllerField, fieldState } = useController({
    name: fieldName,
    control,
  });

  const { value } = controllerField;
  const hasError = !!fieldState.error;
  let isValid = false;

  if (field.required) {
    const isNumberAndNaN = field.type === 'number' && Number.isNaN(value);
    const hasValue = Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== null && value !== '' && !isNumberAndNaN;

    isValid = hasValue && !hasError;
  } else {
    // For optional fields, it's valid if it's empty OR if it has no errors.
    const isFalsy = !value; // Covers null, undefined, '', etc.
    isValid = isFalsy || !hasError;
  }


  // const getFieldValidation = useCallback((field: any) => {
  //   const fieldValue = watch(`${name}.${field.field_id}`);
  //   const fieldError = errors[name]?.[field.field_id] ?? errors[`${name}.${field.field_id}`];

  //   // if (field.type === 'select' && field.selection_type === 'multiple') {
  //   //   console.debug('custom field validation', { field, required: field.required, fieldValue, fieldError });
  //   //   console.debug('isvalid', fieldValue?.length > 0 && !fieldError);
  //   // }


  //   // Field is valid if it has a value and no errors
  //   if (field.required) {
  //     const fieldIsNaN = field.type === 'number' && Number.isNaN(fieldValue);
  //     // console.debug('custom field validation', { 
  //     //   label:field.label ,
  //     //   field, fieldValue, 
  //     //   name,
  //     //   fieldError, fieldIsNaN,
  //     //    errors });
  //     return (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && !fieldIsNaN) && !fieldError;
  //   }
  //   // Optional fields are valid if they're empty or have no errors
  //   return !fieldValue || !fieldError;
  // }, [controllerField.value, fieldState.error, name]);

  const commonProps = {
    // Pass props from the controller and the original field data
    // ...controllerField, // This includes onChange, onBlur, value, ref
    control: control,
    name: fieldName,
    key: field.field_id,
    label: field.label,
    description: field.description,
    info: field.info,
    required: field.required,
    placeholder: field.placeholder || undefined,
    // Validation is now much simpler and more efficient
    isValid: isValid,
    showDescription: false,
    showFormMessage: false,
    maxLength: field.type === 'text' ? 255 : (field.type === 'textarea' ? 5000 : undefined)
  };

  const renderField = () => {
    switch (field.type) {
      case 'text':
        return <CustomTextField {...commonProps} />
      case 'number':
        return <CustomNumberField {...commonProps} decimalPlaces={field.decimal_places} />
      case 'date':
        return <CustomDateField {...commonProps} />
      case 'select':
        return (
          <CustomSelectField
            {...commonProps}
            options={field.data}
            selectionType={field.selection_type}
            allowAdd={field.allow_add}
            onAddOption={onAddOption}
          />
        )
      case 'radio':
        return <CustomRadioField {...commonProps} options={field.data} />
      case 'checkbox':
        return <CustomCheckboxField {...commonProps} />
      case 'switch':
        return <CustomToggleField {...commonProps} />
      case 'textarea':
        return <CustomTextAreaField
          {...commonProps}
          options={field.data}
          allowAdd={field.allow_add}
          onAddOption={onAddOption}
          enableFastFillHighlighting={enableFastFillHighlighting}
        />
      default:
        return <div key={field.field_id}>Unknown field type: {field.type}</div>
    }
  };

  return (
    <FieldContainerComponent
      field_id={fieldName}
      showRightComponent={enableRightComponent}
      isFastFill={field.type === 'textarea' && (field.data?.length > 0 || field.allow_add)}
    >
      {renderField()}
    </FieldContainerComponent>
  );
});
